import { ModalDialog } from '@fukuri-admins/ui/common';
import {
  FileUploadFieldController,
  RadioFieldController,
  ScheduledDateTimeFieldController,
} from '@fukuri-admins/ui/common/Form';
import { CircleQuestionIcon, DownloadIcon } from '@fukuri-admins/ui/common/Icons';
import { buttonVariants } from '@fukuri-admins/ui/shared/buttonVariants';
import { BulkCreationGuideAlert } from './BulkCreationGuideAlert';
import styles from './styles.module.scss';

type Props = {
  /** モーダルのタイトル */
  title: string;
  /** 登録方法の現在値 */
  registrationType: 'immediate' | 'scheduled';
  /** 登録ボタンの disabled 状態 */
  isConfirmDisabled: boolean;
  /** できること・できないことガイドを表示するか */
  showGuideAlert: boolean;
  /** 新デザインの登録方法ラベル（強調色テキスト・新ボタン文言）を表示するか */
  showNewRegistrationLabels: boolean;
  /** モーダルを閉じるコールバック */
  onClose: () => void;
  /** 登録ボタン押下時のコールバック */
  onConfirm: () => void;
  /** テンプレート CSV（UTF-8）ダウンロードボタン押下時のコールバック */
  onDownloadCsvTemplate: () => void;
  /** テンプレート CSV（Shift-JIS）ダウンロードボタン押下時のコールバック */
  onDownloadCsvTemplateSjis: () => void;
};

const REGISTRATION_TYPE_OPTIONS = [
  { value: 'immediate', label: '今すぐ登録する' },
  { value: 'scheduled', label: '登録予約をする' },
];

export const UserBulkCreationPresentation: React.FC<Props> = ({
  title,
  registrationType,
  isConfirmDisabled,
  showGuideAlert,
  showNewRegistrationLabels,
  onClose,
  onConfirm,
  onDownloadCsvTemplate,
  onDownloadCsvTemplateSjis,
}) => (
  <ModalDialog
    isOpen
    title={title}
    description='一括追加・更新の実行時、新しく追加される利用者にはアカウント発行の案内メールが送信されます。'
    onClose={onClose}
    onConfirm={onConfirm}
    confirmText='登録'
    variant='lg'
    isConfirmDisabled={isConfirmDisabled}
  >
    <div className={styles.content}>
      {/* 登録方法セクション */}
      <div className={styles.howToUseSection}>
        <div className={styles.howToUseTitleRow}>
          <span className={styles.howToUseTitle}>登録方法</span>
          <a
            href='https://app.fukuri-app.jp/faq/csv-upload-guide'
            target='_blank'
            rel='noopener noreferrer'
            className={styles.guideLink}
          >
            <CircleQuestionIcon size='xs' />
            CSVファイル作成ガイド
          </a>
        </div>
        <ol className={styles.howToUseList}>
          {showNewRegistrationLabels ? (
            <>
              <li>以下の利用者データ（CSVファイル）をダウンロード</li>
              <li>CSVファイルの2行目から利用者情報を登録</li>
            </>
          ) : (
            <>
              <li>以下のテンプレート（CSVファイル）をダウンロード</li>
              <li>CSVファイルの2行目から従業員情報を登録</li>
            </>
          )}
          <li>CSVファイルをアップロード</li>
        </ol>
        <div className={styles.templateButtons}>
          {showNewRegistrationLabels ? (
            <button
              type='button'
              className={buttonVariants({ variant: 'secondary', size: 'xs' })}
              onClick={onDownloadCsvTemplate}
            >
              <DownloadIcon size='xs' />
              CSVダウンロード（UTF-8）
            </button>
          ) : (
            <a
              href='/templates/creation/user_template_utf.csv'
              download='user_template_utf.csv'
              className={buttonVariants({ variant: 'secondary', size: 'xs' })}
            >
              <DownloadIcon size='xs' />
              テンプレート（UTF-8）
            </a>
          )}
          <span className={styles.templateSeparator}>または</span>
          {showNewRegistrationLabels ? (
            <button
              type='button'
              className={buttonVariants({ variant: 'secondary', size: 'xs' })}
              onClick={onDownloadCsvTemplateSjis}
            >
              <DownloadIcon size='xs' />
              CSVダウンロード（Shift-JIS）
            </button>
          ) : (
            <a
              href='/templates/creation/user_template_sjis.csv'
              download='user_template_sjis.csv'
              className={buttonVariants({ variant: 'secondary', size: 'xs' })}
            >
              <DownloadIcon size='xs' />
              テンプレート（Shift-JIS）
            </a>
          )}
        </div>
      </div>

      {/* CSVファイルアップロード */}
      <FileUploadFieldController
        name='file'
        fieldWrapperProps={{
          labelText: 'CSVファイル',
          beforeMessage:
            '空白行の処理に時間がかかる場合があるため、空白行を削除してからアップロードしてください。',
        }}
        fileUpload={{
          type: 'file',
          accept: ['csv'],
          maxSizeMb: 10,
        }}
      />

      {/* できること・できないこと */}
      {showGuideAlert && <BulkCreationGuideAlert />}

      {/* 実行日時の設定 */}
      <div className={styles.registrationTypeSection}>
        <RadioFieldController
          name='registrationType'
          fieldWrapperProps={{
            labelText: '実行日時の設定',
          }}
          radioGroup={{
            options: REGISTRATION_TYPE_OPTIONS,
            direction: 'column',
            gap: 8,
          }}
        />
        {registrationType === 'scheduled' && (
          <div className={styles.scheduledDateTimeWrapper}>
            <ScheduledDateTimeFieldController />
          </div>
        )}
      </div>
    </div>
  </ModalDialog>
);
